import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";

// Button primitive — composes semantic Tailwind tokens through cva so a
// caller picks a variant + size without re-stating the base classes.
// Mirrors the shape of `@librechat/client/Button` so swapping to the
// published primitive later is a path change, not a signature change.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 font-semibold",
    "rounded-lg transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring/70 focus-visible:ring-offset-0",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-accent-primary text-text-on-accent",
          "hover:bg-accent-primary-hover active:bg-accent-primary-deep",
        ],
        secondary: [
          "bg-surface-tertiary text-text-primary border border-border-light",
          "hover:bg-surface-hover hover:border-border-medium",
        ],
        ghost: [
          "bg-transparent text-text-secondary",
          "hover:bg-surface-hover hover:text-text-primary",
        ],
        destructive: [
          "bg-surface-destructive text-white",
          "hover:brightness-110 active:brightness-95",
        ],
        link: [
          "bg-transparent text-link underline-offset-4",
          "hover:text-link-hover hover:underline",
          "px-0 py-0 rounded-none",
        ],
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-sm",
        icon: "h-9 w-9 p-0",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      block: false,
    },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ButtonVariants {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { className, variant, size, block, type = "button", ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size, block }), className)}
        {...rest}
      />
    );
  },
);

export { buttonVariants };
